import { createAction } from '@reduxjs/toolkit';
import { toPostDataModel } from '../../mappers/posts/toPostDataModel';
import { ymTrackPostBookmark, ymTrackPostDislike, ymTrackPostLike } from '../../utils/metrika';
import { SharingMethod } from '../../utils/share';
import { setBusinessRecords } from '../business/actions';
import { setCategoryRecords } from '../categories/actions';
import { setCollectionsRecords } from '../collections/actions';
import { showNotificationAsync } from '../notifications/actions';
import { UserModel } from '../user/types';
import { PostStatus } from './constants';
import { selectPostById, selectPostCollectionLoading } from './selectors';
import * as postsService from './service';
import { ReactionState, type PostDataModel, type PostModel, type ReportReasons } from './types';
import type { EventSource } from '../../utils/analyticsService';
import type { RootState } from '../store';
import type { AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';

export const removePostRecords = createAction(
    '@@posts/REMOVE_POST_RECORDS',
    (...payload: string[]) => ({ payload }),
);

export const setPostRecords = createAction(
    '@@posts/SET_POST_RECORDS',
    (...payload: PostDataModel[]) => ({ payload }),
);

export const setPostCollection = createAction<{
    namespace: string;
    next?: string;
    identifiers: Array<PostDataModel['id']>;
}>('@@posts/SET_POST_COLLECTION');

export const resetPostCollection = createAction<string>('@@posts/RESET_POST_COLLECTION');

interface SetPostListLoadingPayload {
    namespace: string;
    isLoading: boolean;
}

export const setPostCollectionLoading = createAction<SetPostListLoadingPayload>(
    '@@posts/SET_POST_COLLECTION_LOADING',
);

export const removePostFromBookmarkCollections = createAction<PostDataModel['id']>(
    '@@post/REMOVE_POST_FROM_BOOKMARK_COLLECTIONS',
);

export function loadPostCollectionAsync(
    namespace: string,
    filters: Parameters<typeof postsService.loadFeed>[0],
): ThunkAction<Promise<void>, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const isLoading = selectPostCollectionLoading(getState(), namespace);

        if (isLoading) {
            return;
        }

        dispatch(setPostCollectionLoading({ namespace, isLoading: true }));

        try {
            const response = await postsService.loadFeed(filters);

            dispatch(setBusinessRecords(...response.records.business));
            dispatch(setPostRecords(...response.records.posts));
            dispatch(setCategoryRecords(response.records.categories));
            dispatch(
                setPostCollection({
                    namespace,
                    next: response.next,
                    identifiers: response.posts,
                }),
            );
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось загрузить данные.'));
        }

        dispatch(setPostCollectionLoading({ namespace, isLoading: false }));
    };
}

export function removePostsAsync(
    ...identifiers: string[]
): ThunkAction<Promise<boolean>, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            dispatch(removePostRecords(...identifiers));
            await postsService.removePosts(identifiers);

            return true;
        } catch (error) {
            // TODO: подумать над восстановление в удаленного поста в state
            return false;
        }
    };
}

export function reportPostAsync(
    targetId: string,
    reason: ReportReasons,
): ThunkAction<Promise<void>, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            await postsService.reportPost(targetId, reason);

            dispatch(showNotificationAsync('Мы будем показывать меньше таких публикаций.'));
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось отправить жалобу на пост.'));
        }
    };
}

export function loadPostAsync(
    postId: string,
): ThunkAction<Promise<void>, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            const response = await postsService.loadPostById(postId);

            dispatch(setBusinessRecords(...response.records.business));
            dispatch(setCategoryRecords(response.records.categories));
            dispatch(setPostRecords(...response.records.posts));
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось загрузить данные.'));
        }
    };
}

export const setPostReaction = createAction<{
    id: PostDataModel['id'];
    reactionState: PostDataModel['reactionState'];
}>('@@posts/SET_REACTION');

export function updatePostReactionAsync({
    postId,
    postNumber,
    eventSource,
    reactionState,
}: {
    postId: string;
    postNumber: number;
    eventSource: EventSource;
    reactionState: PostModel['reactionState'];
}): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const model = selectPostById(getState(), postId);

        if (!model) {
            return;
        }

        const currentReactionState =
            reactionState === model.reactionState ? ReactionState.NONE : reactionState;

        dispatch(setPostReaction({ id: postId, reactionState: currentReactionState }));

        try {
            await postsService.updatePostReaction({
                reactionState: currentReactionState,
                model,
                params: { postNumber, eventSource },
            });

            if (currentReactionState === 'like') {
                ymTrackPostLike();
            } else {
                ymTrackPostDislike();
            }
        } catch (error) {
            dispatch(
                setPostReaction({
                    id: postId,
                    reactionState: model.reactionState,
                }),
            );
        }
    };
}

export function bookmarkPostAsync(
    postId: string,
    collectionId?: string,
): ThunkAction<Promise<boolean>, RootState, undefined, AnyAction> {
    return async (dispatch, getState): Promise<boolean> => {
        const model = selectPostById(getState(), postId);

        if (!model) {
            return false;
        }

        // Мы передаем collectionId только в том случае, если хотим удалить пост из конкретной коллекции,
        // а значит не должны снимать с него букмарк.
        if (!collectionId) {
            dispatch(setPostRecords(toPostDataModel({ ...model, saved: !model.saved })));
        }

        try {
            const updatedDefaultCollections = await postsService.bookmarkPost({
                model,
                collectionId,
            });
            dispatch(setCollectionsRecords(updatedDefaultCollections));
            if (model.saved) {
                dispatch(removePostFromBookmarkCollections(model.id));
            } else {
                ymTrackPostBookmark();
            }

            return !model.saved;
        } catch (error) {
            dispatch(setPostRecords(toPostDataModel(model)));
            dispatch(showNotificationAsync('Не удалось сохранить пост'));
        }

        return model.saved;
    };
}

export function updatePostStatusAsync(
    identifiers: string[],
    status: PostStatus,
    user?: UserModel,
): ThunkAction<Promise<boolean>, RootState, undefined, AnyAction> {
    return async (dispatch, getState): Promise<boolean> => {
        const currentState = getState();
        const models: PostModel[] = identifiers
            .map((identifier) => selectPostById(currentState, identifier))
            .filter(Boolean) as any;

        if (models.length === 0) {
            return false;
        }

        try {
            await postsService.bulkUpdateStatus(models, status, user);

            dispatch(setPostRecords(...models.map((m) => toPostDataModel({ ...m, status }))));

            if (status === PostStatus.DELETED) {
                dispatch(removePostRecords(...identifiers));
            }
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось обновить статус'));

            return false;
        }

        return true;
    };
}

export function sharePostAsync(postId: string): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const post = selectPostById(getState(), postId);
        if (!post) {
            return;
        }

        try {
            const sharingMethod = await postsService.sharePost(post);

            if (sharingMethod === SharingMethod.CLIPBOARD) {
                dispatch(showNotificationAsync('Скопировано в буфер обмена'));
            }
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось поделиться данными'));
        }
    };
}
